import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { NewRadio } from '../../../../../Components/PIM/common';
import { CloseIcon } from '../../../../../bombiix-icon';
import { sanitizeHtml } from '../../../DesignDetail/utils/sanitizeHtml';

export interface CanvasCommentPopupProps {
  open: boolean;
  canvasName: string;
  initialComment: string;
  /** The comment icon the popover is anchored to; re-measured as the grid scrolls. */
  anchor: HTMLElement;
  onClose: () => void;
  onSave: (_comment: string, _pushToAll: boolean) => void;
}

const POPOVER_WIDTH = 440;
/** The frame's hug height (node 6657:574521), used until the panel can be measured. */
const POPOVER_HEIGHT = 124;
const ANCHOR_OFFSET = 6;
const VIEWPORT_MARGIN = 8;

/**
 * Place the popover directly under its anchor, horizontally centred on the icon,
 * flipping above only when the panel genuinely would not fit below, and clamping
 * so it never overflows the viewport.
 *
 * `height` must be the panel's real height: a guess that is too large both flips
 * far too eagerly and, when it flips, parks the panel that guessed distance above
 * the icon instead of beside it.
 */
const popoverPosition = (anchor: DOMRect, height: number): { top: number; left: number } => {
  const flipAbove = anchor.bottom + ANCHOR_OFFSET + height > window.innerHeight;
  const top = flipAbove ? Math.max(VIEWPORT_MARGIN, anchor.top - ANCHOR_OFFSET - height) : anchor.bottom + ANCHOR_OFFSET;
  const centred = anchor.left + anchor.width / 2 - POPOVER_WIDTH / 2;
  const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  const left = Math.min(Math.max(VIEWPORT_MARGIN, centred), Math.max(VIEWPORT_MARGIN, maxLeft));
  return { top, left };
};

/**
 * Toolbar order mirrors the Figma frame (node 7968:141350) item for item: bold,
 * italic, underline, fontColor, strikethrough | bulleted, numbered | quote,
 * link. Undo/redo are absent from the design, so they are not registered here.
 * The frame's fourth glyph (layer "Open Chat controls") is the fontColor "A".
 */
const EDITOR_CONFIG = {
  toolbar: ['bold', 'italic', 'underline', 'fontColor', 'strikethrough', '|', 'bulletedList', 'numberedList', '|', 'blockQuote', 'link'],
  link: {
    defaultProtocol: 'https://',
    addTargetToExternalLinks: true,
  },
  htmlSupport: {
    disallow: [{ name: /^(script|style|iframe|object|embed|form|input)$/ }],
    allow: [{ name: 'a', attributes: ['href', 'target', 'rel'], classes: false, styles: false }],
  },
};

/**
 * Per-canvas rich comment editor for the bulk grid. Writes a sanitized
 * `statusComment`; "Push to all selected canvases" applies it to every selected
 * canvas (Figma node 6736:157872).
 *
 * Rendered as a popover anchored to the comment icon — the Figma frame shows it
 * tethered to its cell, not as a centred modal, so there is no dimming backdrop
 * and the grid stays readable behind it. A transparent click-catcher closes it
 * on outside click.
 */
const CanvasCommentPopup: React.FC<CanvasCommentPopupProps> = ({ open, canvasName, initialComment, anchor, onClose, onSave }) => {
  const { t: _t } = useTranslation('bulk-edit');
  /** TranslateFn declared globally in src/global.d.ts */
  const t = _t as unknown as TranslateFn;
  const [comment, setComment] = useState<string>(initialComment);
  const [pushToAll, setPushToAll] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement>(null);
  /* CKEditor re-serialises HTML on load, so compare against the editor's own
     normalized output at load time — not the raw initialComment — to tell whether
     the user actually changed anything. */
  const editorRef = useRef<{ getData: () => string } | null>(null);
  const initialDataRef = useRef<string>(initialComment);
  const frameRef = useRef<number | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>(() => popoverPosition(anchor.getBoundingClientRect(), POPOVER_HEIGHT));

  /* A new config object would re-initialise the editor on every re-render. */
  const editorConfig = useMemo(() => ({ ...EDITOR_CONFIG, placeholder: t('ENTER_COMMENTS') }), [t]);

  /**
   * Re-measure the anchor on scroll and resize so the popover tracks its icon
   * rather than staying where the icon was when it was clicked. The scroll
   * listener is capture-phase: the grid's own scroller does not bubble to window.
   * While the row is virtualised out the element is detached and its rect collapses
   * to zero, so the last good position is kept instead.
   *
   * The panel's own height is read back each time, so a comment tall enough to grow
   * the editor still flips against its real size rather than the frame's default.
   */
  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    const measure = (): void => {
      if (!anchor.isConnected) {
        return;
      }
      const height = panelRef.current?.offsetHeight || POPOVER_HEIGHT;
      const next = popoverPosition(anchor.getBoundingClientRect(), height);
      /* Bail out when nothing moved: re-rendering this tree re-renders CKEditor. */
      setPosition((prev) => (prev.top === next.top && prev.left === next.left ? prev : next));
    };

    /**
     * Coalesce a scroll burst into one measurement per frame. Measuring per event
     * forces a synchronous layout on every one of them, which is what makes the
     * grid stutter while the popover is open.
     */
    const update = (): void => {
      if (frameRef.current !== null) {
        return;
      }
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        measure();
      });
    };

    measure();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [open, anchor]);

  if (!open) {
    return null;
  }

  const { top, left } = position;

  /**
   * The Figma frame carries no Save/Cancel pair — the close cross is the only
   * control — so dismissing the popover (cross or outside click) commits the
   * comment rather than discarding it.
   */
  const closeWithSave = (): void => {
    const currentData = sanitizeHtml(editorRef.current?.getData() ?? comment);
    const changed = currentData !== sanitizeHtml(initialDataRef.current);
    /* Only hit the API when the comment actually changed; "push to all" always
       applies, since an unchanged comment here can still be new for the others. */
    if (changed || pushToAll) {
      onSave(currentData, pushToAll);
    }
    onClose();
  };

  return (
    <>
      <div className="canvas-comment-backdrop" onClick={closeWithSave} aria-hidden="true" />
      <div
        className="canvas-comment-panel"
        ref={panelRef}
        style={{ top, left, width: POPOVER_WIDTH }}
        role="dialog"
        aria-label={`${t('CANVAS_COMMENT_TITLE')} — ${canvasName}`}
      >
        <div className="canvas-comment-panel__editor">
          <CKEditor
            editor={ClassicEditor}
            data={initialComment}
            config={editorConfig}
            onReady={(editor) => {
              if (!editor) {
                return;
              }
              editorRef.current = editor;
              initialDataRef.current = editor.getData();
            }}
            onChange={(_event, editor) => setComment(editor.getData())}
            onBlur={(_event, editor) => setComment(sanitizeHtml(editor.getData()))}
          />
          <button type="button" className="canvas-comment-panel__close" aria-label={t('CLOSE')} onClick={closeWithSave}>
            <CloseIcon size={13} />
          </button>
        </div>
        <div className="canvas-comment-panel__push">
          <NewRadio checked={pushToAll} onChange={() => setPushToAll(true)} onClick={() => setPushToAll((prev) => !prev)} label={t('PUSH_TO_ALL_SELECTED')} />
        </div>
      </div>
    </>
  );
};

export default CanvasCommentPopup;
